import { Product } from "@/components/business/products/ProductCard";

// 🎯 TIPOS PARA EL SISTEMA DE CACHE
export interface CachedProduct {
  data: Product;
  timestamp: number;
  accessCount: number;
  lastAccessed: number;
}

export interface CacheMetrics {
  totalCached: number;
  cacheHits: number;
  cacheMisses: number;
  hitRate: number;
  averageAccessTime: number;
  memoryUsage: number;
}

export interface ProductCacheConfig {
  maxCacheSize: number;
  cacheTime: number; // Tiempo en ms
  enableOffline: boolean;
  enableMetrics: boolean;
  cleanupInterval: number;
}

// 🚀 PRODUCT CACHE SERVICE
export class ProductCacheService {
  private cache = new Map<string, CachedProduct>();
  private metrics: CacheMetrics = {
    totalCached: 0,
    cacheHits: 0,
    cacheMisses: 0,
    hitRate: 0,
    averageAccessTime: 0,
    memoryUsage: 0,
  };
  private config: ProductCacheConfig;
  private accessTimes: number[] = [];

  constructor(config: Partial<ProductCacheConfig> = {}) {
    this.config = {
      maxCacheSize: 100,
      cacheTime: 5 * 60 * 1000, // 5 minutos
      enableOffline: true,
      enableMetrics: true,
      cleanupInterval: 60 * 1000, // 1 minuto
      ...config,
    };

    // 🔄 INICIAR LIMPIEZA AUTOMÁTICA
    this.startCleanupInterval();
  }

  // 🎯 OBTENER PRODUCTO (CACHE-FIRST)
  async getProduct(id: string): Promise<Product | null> {
    const startTime = Date.now();

    try {
      // 1. Verificar cache primero
      const cached = this.cache.get(id);
      if (cached && this.isValid(cached)) {
        this.updateAccessMetrics(cached);
        this.updateMetrics(startTime, true);
        console.log(`🎯 Cache HIT para producto: ${id}`);
        return cached.data;
      }

      // 2. Cache miss - buscar en backend
      // console.log(`❌ Cache MISS para producto: ${id}`);
      this.updateMetrics(startTime, false);

      // 3. Verificar si estamos offline
      if (this.config.enableOffline && !navigator.onLine) {
        console.log("📱 Modo offline - usando cache disponible");
        return cached?.data || null;
      }

      // 4. Fetch del backend
      const product = await this.fetchProductFromBackend(id);

      if (product) {
        // 5. Actualizar cache
        this.setProduct(id, product);
        return product;
      }

      return null;
    } catch (error) {
      console.error(`❌ Error obteniendo producto ${id}:`, error);

      // 6. Fallback a cache si está disponible
      const cached = this.cache.get(id);
      if (cached) {
        console.log(`🔄 Usando cache como fallback para: ${id}`);
        return cached.data;
      }

      return null;
    }
  }

  // 🎯 OBTENER MÚLTIPLES PRODUCTOS (BATCH)
  async getProducts(ids: string[]): Promise<Product[]> {
    const products: Product[] = [];
    const missingIds: string[] = [];

    console.log(`📦 Obteniendo ${ids.length} productos en batch`);

    // 1. Verificar cache primero
    for (const id of ids) {
      const cached = this.cache.get(id);
      if (cached && this.isValid(cached)) {
        this.updateAccessMetrics(cached);
        products.push(cached.data);
        console.log(`🎯 Cache HIT para producto: ${id}`);
      } else {
        missingIds.push(id);
      }
    }

    // 2. Fetch solo los que faltan
    if (missingIds.length > 0) {
      console.log(`📡 Fetching ${missingIds.length} productos del backend`);

      try {
        const fetchedProducts = await this.fetchProductsFromBackend(missingIds);

        fetchedProducts.forEach((product) => {
          this.setProduct(product.id, product);
          products.push(product);
        });
      } catch (error) {
        console.error("❌ Error en batch fetch:", error);

        // Fallback a cache para productos faltantes
        missingIds.forEach((id) => {
          const cached = this.cache.get(id);
          if (cached) {
            products.push(cached.data);
          }
        });
      }
    }

    return products;
  }

  // 🎯 ESTABLECER PRODUCTO EN CACHE
  setProduct(id: string, product: Product): void {
    // Verificar límite de cache
    if (this.cache.size >= this.config.maxCacheSize) {
      this.evictLeastUsed();
    }

    const cachedProduct: CachedProduct = {
      data: product,
      timestamp: Date.now(),
      accessCount: 0,
      lastAccessed: Date.now(),
    };

    this.cache.set(id, cachedProduct);
    this.metrics.totalCached = this.cache.size;

    // console.log(`💾 Producto cacheado: ${id}`);
  }

  // 🎯 INVALIDAR PRODUCTO
  invalidateProduct(id: string): void {
    this.cache.delete(id);
    this.metrics.totalCached = this.cache.size;
    console.log(`🗑️ Producto invalidado: ${id}`);
  }

  // 🎯 INVALIDAR CACHE COMPLETO
  clearCache(): void {
    this.cache.clear();
    this.metrics.totalCached = 0;
    console.log("🧹 Cache limpiado completamente");
  }

  // 🎯 OBTENER MÉTRICAS
  getMetrics(): CacheMetrics {
    return { ...this.metrics };
  }

  // 🎯 OBTENER ESTADO DEL CACHE
  getCacheStatus(): {
    size: number;
    maxSize: number;
    hitRate: number;
    isOnline: boolean;
  } {
    return {
      size: this.cache.size,
      maxSize: this.config.maxCacheSize,
      hitRate: this.metrics.hitRate,
      isOnline: navigator.onLine,
    };
  }

  // 🔄 VERIFICAR SI EL CACHE ES VÁLIDO
  private isValid(cached: CachedProduct): boolean {
    return Date.now() - cached.timestamp < this.config.cacheTime;
  }

  // 🔄 ACTUALIZAR MÉTRICAS DE ACCESO
  private updateAccessMetrics(cached: CachedProduct): void {
    cached.accessCount++;
    cached.lastAccessed = Date.now();
  }

  // 🔄 ACTUALIZAR MÉTRICAS GENERALES
  private updateMetrics(accessTime: number, isHit: boolean): void {
    if (!this.config.enableMetrics) return;

    const processingTime = Date.now() - accessTime;
    this.accessTimes.push(processingTime);

    // Mantener solo los últimos 100 tiempos
    if (this.accessTimes.length > 100) {
      this.accessTimes.shift();
    }

    if (isHit) {
      this.metrics.cacheHits++;
    } else {
      this.metrics.cacheMisses++;
    }

    const total = this.metrics.cacheHits + this.metrics.cacheMisses;
    this.metrics.hitRate =
      total > 0 ? (this.metrics.cacheHits / total) * 100 : 0;
    this.metrics.averageAccessTime =
      this.accessTimes.reduce((sum, time) => sum + time, 0) /
      this.accessTimes.length;
  }

  // 🔄 EVICT LEAST USED (LRU)
  private evictLeastUsed(): void {
    let leastUsed: {
      id: string;
      accessCount: number;
      lastAccessed: number;
    } | null = null;

    for (const [id, cached] of this.cache.entries()) {
      if (
        !leastUsed ||
        cached.accessCount < leastUsed.accessCount ||
        (cached.accessCount === leastUsed.accessCount &&
          cached.lastAccessed < leastUsed.lastAccessed)
      ) {
        leastUsed = {
          id,
          accessCount: cached.accessCount,
          lastAccessed: cached.lastAccessed,
        };
      }
    }

    if (leastUsed) {
      this.cache.delete(leastUsed.id);
      console.log(`🗑️ Evicting least used product: ${leastUsed.id}`);
    }
  }

  // 🔄 LIMPIEZA AUTOMÁTICA
  private startCleanupInterval(): void {
    setInterval(() => {
      this.cleanup();
    }, this.config.cleanupInterval);
  }

  private cleanup(): void {
    const now = Date.now();
    let cleanedCount = 0;

    for (const [id, cached] of this.cache.entries()) {
      if (now - cached.timestamp > this.config.cacheTime) {
        this.cache.delete(id);
        cleanedCount++;
      }
    }

    if (cleanedCount > 0) {
      console.log(`🧹 Cleanup: ${cleanedCount} productos expirados removidos`);
      this.metrics.totalCached = this.cache.size;
    }
  }

  // 📡 FETCH DEL BACKEND
  private async fetchProductFromBackend(id: string): Promise<Product | null> {
    try {
      // Importar apiClient dinámicamente para evitar dependencias circulares
      const { apiClient } = await import("../core/apiClient");

      const response = await apiClient.get(`/api/v1/products/${id}`);
      const backendProduct = response.data;

      // Mapear el formato del backend al formato del frontend
      return this.mapBackendToFrontendProduct(backendProduct);
    } catch (error) {
      console.error(`❌ Error fetching product ${id}:`, error);
      throw error;
    }
  }

  // 📡 FETCH BATCH DEL BACKEND
  private async fetchProductsFromBackend(ids: string[]): Promise<Product[]> {
    try {
      // Importar apiClient dinámicamente para evitar dependencias circulares
      const { apiClient } = await import("../core/apiClient");

      // Como no hay endpoint batch, hacemos requests individuales
      const productPromises = ids.map((id) =>
        apiClient
          .get(`/api/v1/products/${id}`)
          .then((response) => response.data)
      );

      const backendProducts = await Promise.all(productPromises);

      // Mapear todos los productos del backend al formato del frontend
      return backendProducts.map((product) =>
        this.mapBackendToFrontendProduct(product)
      );
    } catch (error) {
      console.error("❌ Error fetching products batch:", error);
      throw error;
    }
  }

  // 🔄 MAPEO BACKEND -> FRONTEND
  private mapBackendToFrontendProduct(backendProduct: any): Product {
    return {
      id: backendProduct.id,
      name: backendProduct.nombre,
      description: backendProduct.descripcion,
      price: Number(backendProduct.precio),
      category:
        backendProduct.category?.nombre || String(backendProduct.category_id),
      image: backendProduct.imagen_url,
      disponible: backendProduct.esta_disponible,
      stock: 0, // Valor por defecto, el backend aún no lo maneja
      rating: undefined, // Campo opcional
    };
  }
}

// 🎯 INSTANCIA GLOBAL DEL PRODUCT CACHE
export const productCache = new ProductCacheService({
  maxCacheSize: 50,
  cacheTime: 5 * 60 * 1000, // 5 minutos
  enableOffline: true,
  enableMetrics: true,
  cleanupInterval: 60 * 1000, // 1 minuto
});
